/**
 * Grundkalender: Karte mit Filterzeile, Raster, Auswahlleiste und Legende. Gewählte Zeiträume,
 * der Apartmentname und „Zeitraum bearbeiten“ führen in den Monat je Apartment, wo die Werte
 * gesetzt werden. Zeitraum und Länge stehen im Bereichskopf.
 */
import { useMemo, useState, type ReactNode } from 'react';
import type { AvailabilityDistributionCalendarResponse } from '@/shared/api';
import { cn } from '@/shared/lib/cn';
import { CalendarGrid } from './CalendarGrid';
import { CalendarLegend } from './CalendarLegend';
import { FullResyncDialog } from './FullResyncDialog';
import { GrundkalenderEmpty } from './GrundkalenderEmpty';
import { GrundkalenderToolbar } from './GrundkalenderToolbar';
import { PeriodPickerButton } from './PeriodPickerButton';
import type { IsoRange } from './rangePickerModel';
import { SelectionBar } from './SelectionBar';
import { useGrundkalender } from './useGrundkalender';
import { CloseoutsOutsideViewNote } from '../CloseoutsOutsideViewNote';

const NO_FAMILIES: ReadonlyMap<string, string> = new Map();

interface GrundkalenderViewProps {
  data: AvailabilityDistributionCalendarResponse;
  canEdit: boolean;
  canFullSync: boolean;
  onChanged: () => void;
  /** Monat je Apartment, auf Wunsch mit den hier gewählten Zeiträumen. */
  onOpenMonth: (propertyId: string, ranges?: IsoRange[]) => void;
  /** Zimmer → Gesamtwohnung aus der Objektwahl; rückt die Zimmer unter ihre Gesamtwohnung. */
  wholeFlatOf?: ReadonlyMap<string, string>;
  /** „verwalten“ im Band der Kanalregeln. */
  onManageRules?: () => void;
  /** Hinweis unter der Filterzeile, etwa „Nachladen fehlgeschlagen“. */
  notice?: ReactNode;
  /** Ein neuer Zeitraum lädt; bis dahin steht der alte gedämpft und nimmt keine Auswahl an. */
  loadingWindow?: boolean;
}

export function GrundkalenderView(props: GrundkalenderViewProps) {
  const { data, canEdit, onChanged, onOpenMonth } = props;
  const state = useGrundkalender(data, props.wholeFlatOf ?? NO_FAMILIES);
  const { selection } = state;
  const [resyncOpen, setResyncOpen] = useState(false);
  const names = useMemo(
    () => new Map(data.apartments.map((apartment) => [apartment.propertyId, apartment.name])),
    [data.apartments],
  );

  return (
    <>
      <section
        aria-label="Grundkalender"
        className="bg-card border-hairline rounded-tile shadow-float overflow-clip border"
      >
        <GrundkalenderToolbar
          apartments={data.apartments}
          filter={state.filter}
          onFilterChange={state.changeFilter}
          canEdit={canEdit}
          canFullSync={props.canFullSync}
          periodAction={
            canEdit && data.apartments.length > 0 ? (
              <PeriodPickerButton
                apartments={data.apartments}
                today={data.objectToday}
                preferredPropertyId={state.focusedPropertyId}
                onPick={(propertyId, range) => onOpenMonth(propertyId, [range])}
              />
            ) : undefined
          }
          onFullResync={() => setResyncOpen(true)}
        />
        {props.notice}
        <div
          aria-busy={props.loadingWindow || undefined}
          className={cn(props.loadingWindow && 'pointer-events-none opacity-60')}
        >
          {state.rows.length === 0 ? (
            <GrundkalenderEmpty
              filtered={data.apartments.length > 0}
              onShowAll={() => state.changeFilter(null)}
            />
          ) : (
            <CalendarGrid
              apartments={state.rows}
              closeouts={data.channelCloseouts}
              today={data.objectToday}
              selectedPropertyId={selection.propertyId}
              ranges={selection.ranges}
              canEdit={canEdit}
              childIds={state.childIds}
              apartmentNames={names}
              onManageRules={props.onManageRules}
              onOpenMonth={(propertyId) => onOpenMonth(propertyId)}
              onBegin={selection.begin}
              onExtend={selection.extend}
            />
          )}
        </div>
        {canEdit && selection.propertyId && (
          <SelectionBar
            apartmentName={names.get(selection.propertyId) ?? ''}
            ranges={selection.ranges}
            onSetValues={() =>
              onOpenMonth(
                selection.propertyId!,
                selection.ranges.map(({ from, to }) => ({ from, to })),
              )
            }
            onClear={selection.clear}
          />
        )}
      </section>
      <CloseoutsOutsideViewNote count={data.channelCloseoutsOutsideView} />
      <CalendarLegend apartments={data.apartments} closeouts={data.channelCloseouts} />
      {resyncOpen && (
        <FullResyncDialog
          apartments={data.apartments}
          preselectedPropertyId={state.focusedPropertyId}
          onClose={() => setResyncOpen(false)}
          onFinished={onChanged}
        />
      )}
    </>
  );
}
